import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { useParams, useNavigate } from 'react-router-dom';
import utils from '../../utils';
import { fetchBonusTypes, fetchBonus, deleteBonus } from '../../database/databaseBonus.js';

export default function AdminBonus() {
  const { clientId, clientName } = useParams();
  const navigate = useNavigate();
  const [bonus, setBonus] = useState([]);
  const [bonusTypes, setBonusTypes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    price: '',
    quantityofclasses: '',
    bonustype: '',
    created_at: '',
    from_date: '',
    to_date: '',
    monthbased: false,
  });

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/admin/clients');
  };

  useEffect(() => {
    (async () => {
      const data = await fetchBonusTypes();
      setBonusTypes(data || []);
    })();
  }, []);

  useEffect(() => {
    if (formData.bonustype) {
      const selectedType = bonusTypes.find((t) => String(t.id) === String(formData.bonustype));
      if (selectedType) {
        setFormData((prev) => ({
          ...prev,
          price: selectedType.price ?? '',
          quantityofclasses: selectedType.sessions ?? '',
        }));
      }
    }
  }, [formData.bonustype, bonusTypes]);

  useEffect(() => {
    if (formData.bonustype) {
      const selectedType = bonusTypes.find((t) => String(t.id) === String(formData.bonustype));

      if (selectedType) {
        setFormData((prev) => ({
          ...prev,
          price: selectedType.price ?? '',
          quantityofclasses: selectedType.sessions ?? '',
          monthbased: selectedType.monthbased ?? false,
        }));
      }
    }
  }, [formData.bonustype, bonusTypes]);

  useEffect(() => {
    const loadBonus = async () => {
      const data = await fetchBonus(clientId);
      setBonus(data || []);
    };

    loadBonus();
  }, [clientId]);

  const openAddModal = () => {
    setEditingId(null);
    setFormData({ bonustype: '', price: '', quantityofclasses: '', created_at: '', from_date: '', to_date: '' });
    setShowModal(true);
  };

  const openEditModal = (bns) => {
    setEditingId(bns.id);
    setFormData({
      bonustype: bns.bonustype?.id ?? '',
      price: bns.price ?? '',
      quantityofclasses: bns.quantityofclasses ?? '',
      created_at: bns.created_at ?? '',
      from_date: bns.from_date ? bns.from_date.slice(0, 7) : '',
      to_date: bns.to_date ?? '',
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const { bonustype, price, quantityofclasses, from_date } = formData;

    // from_date viene como "YYYY-MM"
    let fromDateForDb = null;
    let toDateForDb = null;

    if (from_date) {
      // Primer día del mes
      fromDateForDb = `${from_date}-01`; // "2025-03-01"

      // Último día del mes (opcional, si quieres llenar to_date)
      const [y, m] = from_date.split('-').map(Number); // 2025, 3
      const lastDay = new Date(y, m, 0); // día 0 del mes siguiente = último del actual
      toDateForDb = lastDay.toISOString().slice(0, 10); // "2025-03-31"
    }

    const payload = {
      bonustype: bonustype === '' ? null : Number(bonustype),
      price: price === '' ? null : Number(price),
      quantityofclasses: quantityofclasses === '' ? null : Number(quantityofclasses),
      user_id: editingId ? undefined : clientId,
      from_date: fromDateForDb,
      to_date: toDateForDb,
    };

    if (!payload.bonustype) return alert('Selecciona un tipo de bono.');
    if (payload.quantityofclasses == null || Number.isNaN(payload.quantityofclasses)) return alert('La cantidad de clases es obligatoria.');
    if (payload.price == null || Number.isNaN(payload.price)) return alert('El precio es obligatorio.');

    const { error } = editingId
      ? await supabase
          .from('bonus')
          .update({
            bonustype: payload.bonustype,
            price: payload.price,
            quantityofclasses: payload.quantityofclasses,
            from_date: payload.from_date,
            to_date: payload.to_date,
          })
          .eq('id', editingId)
      : await supabase.from('bonus').insert([
          {
            bonustype: payload.bonustype,
            price: payload.price,
            quantityofclasses: payload.quantityofclasses,
            user_id: clientId,
            from_date: payload.from_date,
            to_date: payload.to_date,
          },
        ]);

    if (error) alert(`Error saving: ${error.message}`);
    else {
      const data = await fetchBonus(clientId);
      setBonus(data || []);
      setShowModal(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este bono definitivamente?')) return;
    await deleteBonus(id);
    fetchBonus(clientId).then((data) => setBonus(data || []));
  };

  return (
    <section className="w-[95%] md:w-[90%] lg:w-[80%] mx-auto py-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-xl sm:text-2xl font-serif text-terracotta">Bonos del Cliente: {clientName}</h2>
        <div className="flex w-full sm:w-auto gap-2">
          <button onClick={goBack} className="w-[50%] sm:w-auto bg-terracotta text-cream px-4 py-2 rounded hover:bg-terracotta-dark transition">
            Volver
          </button>
          <button onClick={openAddModal} className="w-[50%] sm:w-auto bg-terracotta text-cream px-4 py-2 rounded hover:bg-terracotta-dark transition">
            Nuevo Bono
          </button>
        </div>
      </div>

      {/* Desktop Table (>= md) */}
      <div className="hidden md:block overflow-x-auto rounded border border-sand">
        <table className="w-full table-fixed border-collapse text-sm">
          <colgroup>
            {/* Percent-based widths */}
            <col style={{ width: '32%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '15%' }} />
          </colgroup>
          <thead>
            <tr className="bg-sand text-left text-terracotta-dark">
              <th className="border px-3 py-2">Tipo de Bono</th>
              <th className="border px-3 py-2">Fecha</th>
              <th className="border px-3 py-2">Cantidad de Clases</th>
              <th className="border px-3 py-2">Precio</th>
              <th className="border px-3 py-2 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {bonus.map((bns) => (
              <tr key={bns.id} className="hover:bg-sand/40">
                <td className="border px-3 py-2 align-top break-words">{bns.bonustype?.name}</td>
                <td className="border px-3 py-2 align-top whitespace-nowrap">{utils.getMonthYear(bns.from_date)}</td>
                <td className="border px-3 py-2 align-top text-center">{bns.quantityofclasses}</td>
                <td className="border px-3 py-2 align-top whitespace-nowrap">{bns.price}</td>
                <td className="border px-3 py-2 align-top text-center">
                  <div className="flex items-center justify-center gap-3">
                    <button onClick={() => openEditModal(bns)} className="text-blue-600 hover:underline">
                      Editar
                    </button>
                    <button onClick={() => handleDelete(bns.id)} className="text-red-600 hover:underline">
                      Borrar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {bonus.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-4">
                  No hay registros.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile / Small screens: Card list (< md) */}
      <div className="md:hidden space-y-3">
        {bonus.length === 0 && <div className="text-center py-4 border border-sand rounded">No hay registros.</div>}

        {bonus.map((bns) => (
          <div key={bns.id} className="border border-sand rounded-lg p-4 shadow-sm bg-card">
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <span className="w-[40%] text-sm font-medium text-ink/70">Tipo de Bono</span>
                <span className="w-[60%] break-words">{bns.bonustype?.name}</span>
              </div>
              <div className="flex gap-2">
                <span className="w-[40%] text-sm font-medium text-ink/70">Fecha</span>
                <span className="w-[60%]">{utils.getMonthYear(bns.from_date)}</span>
              </div>
              <div className="flex gap-2">
                <span className="w-[40%] text-sm font-medium text-ink/70">Clases</span>
                <span className="w-[60%]">{bns.quantityofclasses}</span>
              </div>
              <div className="flex gap-2">
                <span className="w-[40%] text-sm font-medium text-ink/70">Precio</span>
                <span className="w-[60%] whitespace-nowrap">{bns.price}</span>
              </div>
            </div>

            <div className="mt-4 flex justify-between">
              <button onClick={() => openEditModal(bns)} className="px-3 py-2 rounded border w-[48%] text-blue-700 border-blue-200">
                Editar
              </button>
              <button onClick={() => handleDelete(bns.id)} className="px-3 py-2 rounded border w-[48%] text-red-700 border-red-200">
                Borrar
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-card p-6 rounded-lg w-[96%] sm:w-[85%] md:w-[70%] lg:w-[50%] xl:w-[40%] max-w-[900px] shadow-lg">
            <h3 className="text-lg sm:text-xl font-serif text-terracotta mb-4">{editingId ? 'Editar Bono' : 'Agregar nuevo Bono'}</h3>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <label className="block w-full sm:w-[50%]">
                  <span className="text-sm font-medium">Tipo de Bono</span>
                  <select
                    value={formData.bonustype}
                    onChange={(e) => setFormData({ ...formData, bonustype: e.target.value })}
                    className="w-full border border-sand bg-cream text-ink px-3 py-2 rounded"
                    required
                  >
                    <option value="">Seleccione un tipo de bono</option>
                    {bonusTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block w-full sm:w-[50%]">
                  <span className="text-sm font-medium">Mes</span>
                  <select
                    value={formData.from_date}
                    onChange={(e) => setFormData({ ...formData, from_date: e.target.value })}
                    className="w-full border border-sand bg-cream text-ink px-3 py-2 rounded"
                    required
                  >
                    <option value="">Seleccione un mes</option>
                    {Array.from({ length: 3 }).map((_, index) => {
                      const now = new Date();
                      const year = now.getFullYear();
                      const month = now.getMonth() + index;

                      const safeDate = new Date(year, month, 1); // mes estable sin timezone issues

                      const value = `${safeDate.getFullYear()}-${String(safeDate.getMonth() + 1).padStart(2, '0')}`;

                      return (
                        <option key={value} value={value}>
                          {utils.getMonthYear(safeDate)}
                        </option>
                      );
                    })}
                  </select>
                </label>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <label className="block w-full sm:w-[50%]">
                  <span className="text-sm font-medium">Cantidad de Clases</span>
                  <input
                    type="number"
                    value={formData.quantityofclasses}
                    onChange={(e) => setFormData({ ...formData, quantityofclasses: e.target.value })}
                    className="w-full border border-sand bg-cream text-ink px-3 py-2 rounded"
                    required
                  />
                </label>
                <label className="block w-full sm:w-[50%]">
                  <span className="text-sm font-medium">Precio</span>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full border border-sand bg-cream text-ink px-3 py-2 rounded"
                    required
                  />
                </label>
              </div>
              {/* 
              {formData.monthbased && (
                <div className="p-3 border rounded bg-green-50">
                  <p className="font-medium text-green-700 mb-2">Opciones adicionales para bono mensual</p>

                  <label className="block mb-3">
                    <span className="text-sm font-medium">Repetir por X meses</span>
                    <select className="w-full border px-3 py-2 rounded" onChange={(e) => setFormData({ ...formData, repeatMonths: e.target.value })}>
                      <option value="1">1 mes</option>
                      <option value="2">2 meses</option>
                      <option value="3">3 meses</option>
                      <option value="6">6 meses</option>
                    </select>
                  </label>

                  <label className="block">
                    <span className="text-sm font-medium">Activar renovación automática</span>
                    <input
                      type="checkbox"
                      className="ml-2"
                      checked={formData.autorenew || false}
                      onChange={(e) => setFormData({ ...formData, autorenew: e.target.checked })}
                    />
                  </label>
                </div>
              )} */}

              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 rounded border border-sand text-ink hover:bg-sand/40 w-[45%] sm:w-auto">
                  Cancelar
                </button>
                <button type="submit" className="bg-terracotta text-cream px-4 py-2 rounded hover:bg-terracotta-dark transition w-[45%] sm:w-auto">
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
